import React, { useEffect, useMemo, useRef } from 'react';
import LocalOpsSheet from './LocalOpsSheet';

const formatPrice = (price = {}) =>
  price.display ||
  (price.amountCents ? `$${(price.amountCents / 100).toFixed(2)}` : '$9.99');

const formatApproxCurrency = (amount) => {
  const number = Number(amount);
  if (!Number.isFinite(number) || number <= 0) return '';
  return `$${Math.round(number).toLocaleString('en-US')}`;
};

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export default function LocalOpsFoundingHelperPassModal({
  open,
  offer,
  busy = false,
  error = '',
  onPrimary,
  onSkip,
  onDismiss,
}) {
  const primaryRef = useRef(null);
  const region = offer?.regionDisplayName || 'Local';
  const offerName = offer?.offerName || `${region} Founding Helper Pass`;
  const priceLabel = useMemo(() => formatPrice(offer?.price), [offer?.price]);
  const benefits = offer?.benefits || {};
  const firstLookMinutes = toPositiveNumber(
    benefits.firstLookTasks?.windowMinutes,
  );
  const feeTier = benefits.helperFeeTier || {};
  const standardFeePercent = toPositiveNumber(feeTier.standardFeePercent);
  const founderFeePercent = toPositiveNumber(feeTier.founderFeePercent);
  const computedDiscount =
    standardFeePercent && founderFeePercent
      ? standardFeePercent - founderFeePercent
      : null;
  const discountPercent =
    toPositiveNumber(feeTier.discountPercent) ||
    (computedDiscount && computedDiscount > 0 ? computedDiscount : null);
  const priceAmountCents = toPositiveNumber(offer?.price?.amountCents);
  const breakEvenEarningsLabel =
    priceAmountCents && discountPercent
      ? formatApproxCurrency(priceAmountCents / discountPercent)
      : '';
  const firstLookTitle = firstLookMinutes
    ? `${firstLookMinutes}-minute first-look access`
    : 'First-look access';
  const founderFeeTitle = founderFeePercent
    ? `${founderFeePercent}% helper task fee during launch`
    : 'Lower helper task fee during launch';
  const founderFeeCopy =
    standardFeePercent && founderFeePercent
      ? `Standard launch fee is ${standardFeePercent}%, so Founding Helpers keep more on eligible completed task earnings.`
      : 'Founding Helpers keep more on eligible completed task earnings during launch.';
  const mathHasNumbers = standardFeePercent && founderFeePercent;

  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => {
      primaryRef.current?.focus?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  return (
    <LocalOpsSheet
      open={open}
      onClose={onDismiss}
      title={offerName}
      ariaLabel={offerName}
      className="localops-sheet--centered localops-founding-pass"
    >
      <div className="localops-founding-pass__body">
        <div className="localops-founding-pass__eyebrow">
          {region.toUpperCase()} LAUNCH / FOUNDING RATE
        </div>
        <p className="localops-founding-pass__hero">
          Get in the founder lane while it's still open.
        </p>
        <p className="localops-founding-pass__intro">
          {region} LocalOps is opening in stages. Founding Helpers get early
          launch advantages before the helper network opens wider, including
          first-look access when eligible paid tasks are posted.
        </p>

        <div className="localops-founding-pass__benefits">
          <div className="localops-founding-pass__benefit">
            <strong>{firstLookTitle}</strong>
            <span>
              Get the first chance to accept eligible paid tasks before they open
              wider.
            </span>
          </div>
          <div className="localops-founding-pass__benefit">
            <strong>Founding Helper status</strong>
            <span>Show you were early, serious, and ready on JG Player.</span>
          </div>
          <div className="localops-founding-pass__benefit">
            <strong>{founderFeeTitle}</strong>
            <span>{founderFeeCopy}</span>
          </div>
          <div className="localops-founding-pass__benefit">
            <strong>Priority access if helper setup is waitlisted</strong>
            <span>
              If {region} helper setup gets limited, Founding Helpers get the
              priority lane.
            </span>
          </div>
        </div>

        {mathHasNumbers ? (
          <details className="localops-founding-pass__math">
            <summary>How the math works</summary>
            <div>
              <p>Standard helper task fee: {standardFeePercent}%</p>
              <p>Founding Helper task fee: {founderFeePercent}%</p>
              {discountPercent ? (
                <p>
                  That means Founding Helpers keep an extra {discountPercent}% on
                  eligible completed task earnings during launch.
                </p>
              ) : null}
              {breakEvenEarningsLabel ? (
                <p>
                  At that difference, the {priceLabel} pass can pay for itself
                  after about {breakEvenEarningsLabel} in eligible completed
                  task earnings.
                </p>
              ) : null}
              <p>
                No guaranteed tasks or earnings. Safety, location, trust, helper
                setup, and task requirements still apply.
              </p>
            </div>
          </details>
        ) : null}

        <div className="localops-founding-pass__price">
          <strong>One-time founding rate: {priceLabel}</strong>
          <span>No monthly fee. No subscription.</span>
        </div>

        <p className="localops-founding-pass__fomo">
          This is a launch-window offer. The founding rate, benefits, and
          availability may change as {region} fills in.
        </p>
        <p className="localops-founding-pass__disclaimer">
          No guaranteed tasks or earnings. Safety, location, trust, helper setup,
          and task requirements still apply.
        </p>
      </div>

      {error ? (
        <div className="localops-founding-pass__error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="localops-founding-pass__actions">
        <button
          type="button"
          className="localops-btn localops-founding-pass__primary"
          onClick={onPrimary}
          disabled={busy}
          ref={primaryRef}
        >
          {busy
            ? 'Opening Stripe...'
            : `Claim my Founding Helper Pass - ${priceLabel}`}
        </button>
        <button
          type="button"
          className="localops-btn localops-btn--ghost localops-founding-pass__secondary"
          onClick={onSkip}
          disabled={busy}
        >
          Continue without pass
        </button>
      </div>
    </LocalOpsSheet>
  );
}
